<template>
    <table class="w-full col-span-2 text-xs">
        <tr class="border-b">
            <th class="p-2 border text-left">{{ $t('amendments.quote') }}</th>
            <th class="p-2 border text-left">{{ $t('commons.type') }}</th>
            <th class="p-2 border text-right">{{ $t('invoices.quoteAmountTTC') }}</th>
            <th class="p-2 border text-right">{{ $t('invoices.priorRemainingAmountTTC') }}</th>
            <th class="p-2 border text-right bg-blue-200">
                <span :title="$t('invoices.subAmountTTCTips')">{{ $t('invoices.subAmountTTC') }}</span>
            </th>
            <th class="p-2 border text-right">{{ $t('invoices.remainingAmountTTC') }}</th>
        </tr>
        <tr class="border-b" v-for="amendment in chargedAmendments">
            <td class="border p-2">
                <router-link
                    class="hover:underline"
                    :to="{
                        name: 'followAmendment',
                        params: {
                            ...$route.params,
                            amendmentId: amendment.id,
                        },
                    }"
                >
                    <span class="text-xs">{{ amendment.code }}&nbsp;</span>
                    <span>{{ amendment.object }}</span>
                </router-link>
            </td>
            <td class="border p-2">
                {{ amendment.type ? $t('invoices.amendmentSimplifiedTypes.' + amendment.type) : '' }}
            </td>
            <td class="border p-2 text-right">
                <span>
                    {{ amendment.amountTTC ? $n(amendment.amountTTC, currencyFormat) : '-' }}
                </span>
            </td>
            <td class="border p-2">
                <div class="flex justify-end gap-1 items-center">
                    <span>
                        {{ amendment.remainingAmountTTC ? $n(amendment.remainingAmountTTC, currencyFormat) : '-' }}
                    </span>
                </div>
            </td>
            <td class="border p-2 text-right bg-blue-100">
                <div
                    v-if="
                        amendment.quoteStatus !== 'validated' ||
                        amendment.amendmentStatus !== 'signed' ||
                        amendment.SOStatus !== 'signed'
                    "
                    :title="
                        amendment.quoteStatus !== 'validated'
                            ? $t('invoices.invoiceReadyNotValidatedQuote')
                            : amendment.amendmentStatus !== 'signed'
                            ? $t('invoices.invoiceReadyNotSignedAmendment')
                            : amendment.SOStatus !== 'signed'
                            ? $t('invoices.invoiceReadyNotSignedSO')
                            : ''
                    "
                    class="text-red-500 w-full flex justify-center"
                >
                    <icon-alert-outline width="16px" height="16px" />
                </div>
                <div v-else class="flex justify-between items-center">
                    <app-button
                        v-if="amendment.remainingAmountTTC > 0"
                        size="mini"
                        variant="borderless"
                        icon="icon-arrow-right-bold-box"
                        class="opacity-10 hover:opacity-100"
                        :title="$t('invoices.copyRemaining')"
                        @click="copyRemainingAmountToChargedAmount(amendment)"
                    ></app-button>
                    <app-number-link
                        :show-label="false"
                        :label="$t('invoices.subAmountTTC')"
                        v-model="amendment.chargedAmountTTC"
                        :positiveOnly="false"
                        format="decimal"
                        @input="onAmendmentAmountChanged(amendment)"
                        :currency="currencyFormat.currency"
                        :disabled="
                            amendment.quoteStatus !== 'validated' ||
                            amendment.amendmentStatus !== 'signed' ||
                            amendment.SOStatus !== 'signed'
                        "
                    >
                        <div class="flex gap-1">
                            <u class="w-full text-right">
                                {{ $n(amendment.chargedAmountTTC || 0, currencyFormat) }}
                            </u>
                            <span
                                :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                v-if="amendment.amountTTC && amendment.amountTTC > 0"
                                :class="{
                                    'text-red-500 font-bold': amendment.chargedAmountTTC > amendment.amountTTC,
                                }"
                            >
                                ({{ Math.round((amendment.chargedAmountTTC / amendment.amountTTC) * 100) }}%)
                            </span>
                        </div>
                    </app-number-link>
                </div>
            </td>
            <td class="border p-2 text-right">
                <span>
                    {{
                        amendment.remainingAmountTTC && amendment.chargedAmountTTC
                            ? $n(amendment.remainingAmountTTC - amendment.chargedAmountTTC, currencyFormat)
                            : '-'
                    }}
                </span>
                <span
                    :title="$t('invoices.invoicePercentOfQuoteAmount')"
                    v-if="amendment.amountTTC && amendment.remainingAmountTTC && amendment.chargedAmountTTC"
                    :class="{
                        'text-red-500 font-bold': amendment.remainingAmountTTC < amendment.chargedAmountTTC,
                    }"
                >
                    ({{ Math.round((amendment.chargedAmountTTC / amendment.remainingAmountTTC) * 100) }}%)
                </span>
            </td>
        </tr>
        <tr>
            <td colspan="2"></td>
            <td class="border p-2 text-right">{{ $n(totalQuotes, currencyFormat) }}</td>
            <td class="border p-2 text-right">
                {{ $n(totalPriorRemainingAmountTTC, currencyFormat) }}
            </td>
            <td class="border p-2">
                <div
                    class="flex justify-center gap-1"
                    :class="{
                        'text-red-500 font-bold': totalChargedAmendments !== invoice.amountTTC,
                    }"
                    :title="
                        totalChargedAmendments !== invoice.amountTTC
                            ? $t('invoices.distributedAmountDoesNotMatchInvoiceAmount')
                            : ''
                    "
                >
                    <span>
                        {{ $n(totalChargedAmendments, currencyFormat) }}
                    </span>
                </div>
            </td>
            <td class="border p-2 text-right">{{ $n(totalRemainingAmountTTC, currencyFormat) }}</td>
        </tr>
    </table>
</template>

<script>
import { reduceSumOn, sortBy } from '@/services/sanitize.service';
import { getAmountTTCAlreadyChargedByAmendmentId, getInvoice, getInvoices } from './invoices.service';
import { combineLatest } from 'rxjs';
import { queryProject } from '@/features/projects/projects.service';
import { getAmendments } from '@/features/amendments/amendments.service';
import { getCIEs } from '@/features/cie/CIE.service';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import AppButton from '@/components/appButton/AppButton.vue';

export default {
    components: {
        AppButton,
        AppNumberLink,
        AppFieldset,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
            this.project = project;
        });
        this.init();
    },
    watch: {
        $route: () => this.init(),
    },
    computed: {
        totalChargedAmendments() {
            return reduceSumOn(this.chargedAmendments, 'chargedAmountTTC');
        },
        totalQuotes() {
            return reduceSumOn(this.chargedAmendments, 'amountTTC');
        },
        totalPriorRemainingAmountTTC() {
            return reduceSumOn(this.chargedAmendments, 'remainingAmountTTC');
        },
        totalRemainingAmountTTC() {
            return reduceSumOn(this.chargedAmendments, (item) =>
                item.remainingAmountTTC && item.chargedAmountTTC ? item.remainingAmountTTC - item.chargedAmountTTC : 0,
            );
        },
    },
    props: { distributionKey: { type: String } },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getInvoice(this.$route.params.projectId, this.$route.params.invoiceId),
                    getInvoices(this.$route.params.projectId),
                    getAmendments(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                ]).subscribe(async ([invoice, allInvoices, amendments, cie]) => {
                    const relevantCie = cie.filter((cie) => cie.quoteStatus && cie.quoteStatus === 'validated');
                    const amountTTCAlreadyChargedByAmendmentId = getAmountTTCAlreadyChargedByAmendmentId(
                        allInvoices,
                        invoice,
                    );
                    this.invoice = invoice;
                    this.amendments = sortBy(
                        [
                            ...amendments.filter((amendment) => amendment.quoteStatus === 'validated'),
                            ...relevantCie
                                .filter((cie) => cie.bundleId === invoice.emitterId)
                                .map((cie) => ({ ...cie, type: 'cie' })),
                        ],
                        (amendment) => (amendment.code || 'zzz') + amendment.object,
                    );

                    this.chargedAmendments = this.amendments
                        .filter((amendment) => amendment.bundleId === invoice.emitterId)
                        .map((amendment) => ({
                            ...amendment,
                            remainingAmountTTC:
                                amendment.amountTTC - (amountTTCAlreadyChargedByAmendmentId[amendment.id] || 0),
                            chargedAmountTTC:
                                this.invoice[this.distributionKey].find(
                                    (chargedAmendment) => chargedAmendment.id === amendment.id,
                                )?.amountTTC || 0,
                        }));
                    console.log(this.invoice[this.distributionKey]);
                }),
            ];
        },
        copyRemainingAmountToChargedAmount(amendment) {
            amendment.chargedAmountTTC = amendment.remainingAmountTTC;
            this.onAmendmentAmountChanged();
        },
        onAmendmentAmountChanged() {
            console.log(
                this.chargedAmendments
                    .map((chargedAmendment) => ({
                        id: chargedAmendment.id,
                        amountTTC: chargedAmendment.chargedAmountTTC,
                    }))
                    .filter((chargedAmendment) => chargedAmendment.amountTTC !== 0),
            );
            this.$emit(
                'distributionChanged',
                this.chargedAmendments
                    .map((chargedAmendment) => ({
                        id: chargedAmendment.id,
                        amountTTC: chargedAmendment.chargedAmountTTC,
                    }))
                    .filter((chargedAmendment) => chargedAmendment.amountTTC !== 0),
            );
        },
    },
    data() {
        return {
            amendments: [],
            chargedAmendments: [],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            subcontractors: [],
            subscriptions: [],
            bundleMap: {},
            bundles: [],
            invoice: {},
            project: {},
        };
    },
};
</script>
